/**
 * Spatial layout for Grid AI automation.
 * Places newly added objects relative to anchors ("on top of", "left of", …)
 * using world-space bounding boxes so stacks sit correctly.
 */

import * as THREE from 'three';

const SHAPE_TYPES = [
  'cube',
  'sphere',
  'cylinder',
  'cone',
  'pyramid',
  'capsule',
  'torus',
  'rectangle',
  'ellipse',
  'triangle',
  'plane',
];

export const PLACEMENT_TYPES = [
  'on_top',
  'above',
  'below',
  'under',
  'left',
  'right',
  'front',
  'behind',
  'next_to',
];

const RELATION_PATTERNS = [
  {
    re: /\b([a-z0-9_]+)\s+on\s+top\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'on_top',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+above\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'above',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:below|under|underneath)\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'below',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:to\s+the\s+)?left\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'left',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:to\s+the\s+)?right\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'right',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+in\s+front\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'front',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+behind\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'behind',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:next\s+to|beside|alongside)\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'next_to',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  // "add a cube with a cone on top"
  {
    re: /\b(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\s+with\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\s+on\s+top\b/i,
    placement: 'on_top',
    subjectIdx: 2,
    anchorIdx: 1,
  },
  // "stack a cone on a cube"
  {
    re: /\bstack\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\s+on\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'on_top',
    subjectIdx: 1,
    anchorIdx: 2,
  },
];

const SKIP_WORDS = new Set([
  'add',
  'create',
  'make',
  'put',
  'place',
  'the',
  'a',
  'an',
  'and',
  'with',
  'some',
  'to',
  'scene',
]);

function normalizeType(word) {
  const w = String(word || '')
    .toLowerCase()
    .replace(/s$/, ''); // cubes → cube (simple)
  // undo over-stem for "glass" etc. — only strip trailing s for known shapes
  const raw = String(word || '').toLowerCase();
  if (SHAPE_TYPES.includes(raw)) return raw;
  if (SHAPE_TYPES.includes(w)) return w;
  // lights / content
  const aliases = {
    cubes: 'cube',
    spheres: 'sphere',
    cylinders: 'cylinder',
    cones: 'cone',
    pyramids: 'pyramid',
  };
  return aliases[raw] || raw;
}

/**
 * Parse "cone on top of a cube" style relations from free text.
 * @returns {{ subject: string, anchor: string, placement: string } | null}
 */
export function parseSpatialRelation(userCommand) {
  const text = String(userCommand || '').trim();
  if (!text) return null;

  for (const pattern of RELATION_PATTERNS) {
    const m = text.match(pattern.re);
    if (!m) continue;
    const subject = normalizeType(m[pattern.subjectIdx]);
    const anchor = normalizeType(m[pattern.anchorIdx]);
    if (!subject || !anchor || subject === anchor) continue;
    if (SKIP_WORDS.has(subject) || SKIP_WORDS.has(anchor)) continue;
    return {
      subject,
      anchor,
      placement: pattern.placement,
    };
  }

  return null;
}

/**
 * Rewrite / inject tool calls so the anchor is created first and the subject
 * has relativeTo + placement. Safe no-op when no relation is detected.
 * If the command refers to "the cube" and that shape already exists, skip recreating it.
 */
export function enrichSpatialToolCalls(userCommand, toolCalls, scene = null) {
  const relation = parseSpatialRelation(userCommand);
  if (!relation || !Array.isArray(toolCalls)) return toolCalls;

  const { subject, anchor, placement } = relation;
  const calls = toolCalls.map((c) =>
    c && typeof c === 'object'
      ? { ...c, parameters: { ...(c.parameters || {}) } }
      : c
  );

  const isAddShape = (call, type) => {
    if (!call || call.name !== 'add_object') return false;
    const t = String(call.parameters?.objectType || '').toLowerCase();
    return t === type;
  };

  const refersToExisting = /\bthe\s+/i.test(String(userCommand || ''));
  const existingAnchor =
    scene && refersToExisting ? findAnchorObject(scene, anchor) : null;

  let anchorCall = existingAnchor
    ? null
    : calls.find((c) => isAddShape(c, anchor));
  let subjectCall = calls.find((c) => isAddShape(c, subject));

  if (!existingAnchor && !anchorCall) {
    anchorCall = {
      name: 'add_object',
      parameters: { objectType: anchor, quantity: 1 },
    };
  }
  if (!subjectCall) {
    subjectCall = {
      name: 'add_object',
      parameters: { objectType: subject, quantity: 1 },
    };
  }

  subjectCall.parameters = {
    ...subjectCall.parameters,
    objectType: subject,
    relativeTo: anchor,
    placement,
  };
  delete subjectCall.parameters.position;
  if (subjectCall.parameters.properties) {
    const props = { ...subjectCall.parameters.properties };
    delete props.position;
    subjectCall.parameters.properties = props;
  }

  if (anchorCall) {
    anchorCall.parameters = {
      ...anchorCall.parameters,
      objectType: anchor,
    };
    delete anchorCall.parameters.relativeTo;
    delete anchorCall.parameters.placement;
  }

  const others = calls.filter(
    (c) => c && !isAddShape(c, anchor) && !isAddShape(c, subject)
  );

  return anchorCall
    ? [anchorCall, subjectCall, ...others]
    : [subjectCall, ...others];
}

/**
 * Find the best anchor object in the scene for a type/name hint.
 * Prefers the most recently added matching shape.
 */
export function findAnchorObject(scene, typeOrName) {
  if (!scene || !typeOrName) return null;
  const want = String(typeOrName).toLowerCase().trim();
  if (!want) return null;

  const matches = [];
  scene.traverse((obj) => {
    if (!obj || (!obj.isObject3D && !obj.isMesh && !obj.isGroup)) return;
    if (obj.isScene) return;
    // Skip pure helpers / cameras / lights
    if (obj.isCamera || obj.isLight || obj.isHelper) return;

    const meshCode = String(
      obj.mesh_code || obj.userData?.mesh_code || obj.userData?.originalType || ''
    ).toLowerCase();
    const fileName = String(obj.file_name || obj.userData?.file_name || '').toLowerCase();
    const name = String(obj.name || '').toLowerCase();
    const type = String(obj.userData?.type || '').toLowerCase();

    const isShape = type === 'shape' || obj.isShapes || obj.glb_type === 'shapes';
    const codeMatch =
      meshCode === want ||
      meshCode === want.replace(/s$/, '') ||
      (want && meshCode.includes(want));
    const nameMatch =
      fileName === want ||
      fileName.startsWith(want) ||
      (fileName && fileName.includes(want));

    if (codeMatch || (isShape && nameMatch) || nameMatch) {
      matches.push(obj);
    }
  });

  if (!matches.length) return null;
  // Last match ≈ most recently added among traversed objects; prefer direct scene children order
  const children = scene.children || [];
  let best = matches[matches.length - 1];
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i];
    if (matches.includes(child)) {
      best = child;
      break;
    }
    // nested
    const nested = matches.find((m) => child === m || child.children?.includes?.(m));
    if (nested) {
      best = nested;
      break;
    }
  }
  return best;
}

/**
 * Move `object` so it sits relative to `anchor` per placement.
 * Uses world bounding boxes (works for centered Three.js primitives).
 */
export function applyRelativePlacement(
  object,
  anchor,
  placement,
  options = {}
) {
  if (!object || !anchor || !object.position) return false;

  const gap = Number(options.gap);
  const clearGap = Number.isFinite(gap) ? gap : 0.02;

  // Ensure matrices are current before measuring
  anchor.updateMatrixWorld?.(true);
  object.updateMatrixWorld?.(true);

  const anchorBox = new THREE.Box3().setFromObject(anchor);
  const movingBox = new THREE.Box3().setFromObject(object);

  if (anchorBox.isEmpty() || movingBox.isEmpty()) return false;

  const anchorCenter = anchorBox.getCenter(new THREE.Vector3());
  const movingCenter = movingBox.getCenter(new THREE.Vector3());

  // Vector from object.position to its bbox center (handles centered geometries)
  const centerOffset = movingCenter.clone().sub(object.position);
  const bottomOffset = movingBox.min.y - object.position.y;
  const topOffset = movingBox.max.y - object.position.y;

  let targetPos = new THREE.Vector3();

  const rel = String(placement || 'on_top').toLowerCase();

  switch (rel) {
    case 'on_top':
    case 'above':
      // Rest subject's bottom on anchor's top
      targetPos.set(
        anchorCenter.x - centerOffset.x,
        anchorBox.max.y + clearGap - bottomOffset,
        anchorCenter.z - centerOffset.z
      );
      break;
    case 'below':
    case 'under':
      targetPos.set(
        anchorCenter.x - centerOffset.x,
        anchorBox.min.y - clearGap - topOffset,
        anchorCenter.z - centerOffset.z
      );
      break;
    case 'left':
      {
        const leftOffset = movingBox.max.x - object.position.x;
        targetPos.set(
          anchorBox.min.x - clearGap - leftOffset,
          anchorCenter.y - centerOffset.y,
          anchorCenter.z - centerOffset.z
        );
      }
      break;
    case 'right':
    case 'next_to':
      {
        const rightOffset = movingBox.min.x - object.position.x;
        targetPos.set(
          anchorBox.max.x + clearGap - rightOffset,
          anchorCenter.y - centerOffset.y,
          anchorCenter.z - centerOffset.z
        );
      }
      break;
    case 'front':
      {
        const frontOffset = movingBox.min.z - object.position.z;
        targetPos.set(
          anchorCenter.x - centerOffset.x,
          anchorCenter.y - centerOffset.y,
          anchorBox.max.z + clearGap - frontOffset
        );
      }
      break;
    case 'behind':
      {
        const backOffset = movingBox.max.z - object.position.z;
        targetPos.set(
          anchorCenter.x - centerOffset.x,
          anchorCenter.y - centerOffset.y,
          anchorBox.min.z - clearGap - backOffset
        );
      }
      break;
    default:
      targetPos.set(
        anchorCenter.x - centerOffset.x,
        anchorBox.max.y + clearGap - bottomOffset,
        anchorCenter.z - centerOffset.z
      );
  }

  object.position.copy(targetPos);
  object.updateMatrixWorld?.(true);
  return true;
}

/**
 * Normalize placement aliases from AI output.
 */
export function normalizePlacement(value) {
  const v = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const map = {
    on_top: 'on_top',
    ontop: 'on_top',
    top: 'on_top',
    above: 'above',
    over: 'on_top',
    below: 'below',
    under: 'below',
    underneath: 'below',
    left: 'left',
    left_of: 'left',
    right: 'right',
    right_of: 'right',
    front: 'front',
    in_front: 'front',
    in_front_of: 'front',
    behind: 'behind',
    back: 'behind',
    next_to: 'next_to',
    nextto: 'next_to',
    beside: 'next_to',
    alongside: 'next_to',
  };
  return map[v] || (PLACEMENT_TYPES.includes(v) ? v : null);
}

// ─── Multi-object group layouts (line / grid / circle / stack) ─────────

const WORD_QUANTITIES = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
};

/**
 * Parse "five", "5 cubes", "add 3 …" as a count.
 * Ignores angles ("90°"), percents, and grid sizes ("3x3") so they are never
 * mistaken for quantity (e.g. "torus rotated 90°" must stay quantity 1).
 */
export function parseQuantityFromText(userCommand) {
  const original = String(userCommand || '');
  const text = original.toLowerCase();
  if (!text.trim()) return null;

  // Remove non-count numeric contexts before looking for a quantity
  const stripped = text
    // 90°, 90 deg, 90 degrees, 1.5 rad
    .replace(
      /-?\d+(?:\.\d+)?\s*(?:°|deg(?:rees?)?|radians?|rad)\b/gi,
      ' '
    )
    // rotated 90 / rotation: 90 / angle 45
    .replace(
      /\b(?:rotated?|rotation|angle|pitch|yaw|roll|tilt(?:ed)?)\s*[:=]?\s*-?\d+(?:\.\d+)?/gi,
      ' '
    )
    // 90 on x axis / 90 around y (degree symbol optional after strip above)
    .replace(
      /\b-?\d+(?:\.\d+)?\s*(?:on|around|about|along)\s+(?:the\s+)?[xyz]\b/gi,
      ' '
    )
    // percentages / opacity
    .replace(/\b\d+(?:\.\d+)?%/g, ' ')
    // grid "3x3" / "3×4"
    .replace(/\b\d+\s*[x×]\s*\d+\b/gi, ' ');

  // Explicit: "add 5 …" / "create 12 …"
  const afterVerb = stripped.match(
    /\b(?:add|create|make|place|put|spawn|insert)\s+(\d{1,3})\b/
  );
  if (afterVerb) {
    const n = parseInt(afterVerb[1], 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(n, 100);
  }

  // "5 blue cubes" / "5 cylinders" / "5 objects"
  const shapeWords =
    'cubes?|spheres?|cylinders?|cones?|pyramids?|capsules?|tori|toruses?|rectangles?|ellipses?|triangles?|planes?|objects?|shapes?|models?|meshes?|lights?|texts?|hotspots?|boxes?';
  const beforeShape = stripped.match(
    new RegExp(`\\b(\\d{1,3})\\s+(?:[a-z-]+\\s+){0,4}(?:${shapeWords})\\b`, 'i')
  );
  if (beforeShape) {
    const n = parseInt(beforeShape[1], 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(n, 100);
  }

  // Word quantities near a shape: "five blue cylinders"
  for (const [word, n] of Object.entries(WORD_QUANTITIES)) {
    if (word === 'a' || word === 'an') continue;
    if (
      new RegExp(
        `\\b${word}\\s+(?:[a-z-]+\\s+){0,4}(?:${shapeWords})\\b`,
        'i'
      ).test(stripped)
    ) {
      return n;
    }
    // "add five …" without relying on shape word order edge cases
    if (
      new RegExp(
        `\\b(?:add|create|make|place|put)\\s+${word}\\b`,
        'i'
      ).test(stripped)
    ) {
      return n;
    }
  }

  // Singular article → not a multi-add
  if (/\b(?:add|create|make|place|put)\s+(?:a|an)\b/.test(stripped)) {
    return null;
  }

  return null;
}

/**
 * Detect how multiple copies should be arranged.
 * CRITICAL: quantity > 1 always gets a layout (default: line on X)
 * so objects never pile on the same origin.
 *
 * Orientation is resolved independently so phrases like
 * "straight line vertically" correctly use the Y axis.
 */
export function parseGroupLayout(userCommand, quantityHint = 1) {
  const text = String(userCommand || '').toLowerCase();
  const quantity =
    parseQuantityFromText(text) ||
    (Number.isFinite(quantityHint) ? quantityHint : 1);

  let layout = null;
  let spacing = 1.5;
  let cols = null;

  // Stack / on top of each other (vertical pile)
  if (
    /\bstack(?:ed|s)?\b/.test(text) ||
    /\bon\s+top\s+of\s+(?:each\s+other|one\s+another)\b/.test(text)
  ) {
    layout = 'stack';
  } else if (/\b(?:in\s+a\s+)?(?:circle|ring)\b|\barranged\s+around\b/.test(text)) {
    layout = 'circle';
  } else if (/\bgrid\b|\bmatrix\b|\brows?\s+(?:and|&)\s+columns?\b/.test(text)) {
    layout = 'grid';
    const dim = text.match(/\b(\d+)\s*[x×]\s*(\d+)\b/);
    if (dim) cols = parseInt(dim[2], 10);
  } else if (
    /\b(straight\s+)?lines?\b/.test(text) ||
    /\bin\s+a\s+row\b/.test(text) ||
    /\bin\s+a\s+column\b/.test(text) ||
    /\bside\s+by\s+side\b/.test(text) ||
    /\bhorizontal(?:ly)?\b/.test(text) ||
    /\bvertical(?:ly)?\b/.test(text) ||
    /\bcolumns?\b/.test(text) ||
    /\bone\s+above\s+(?:the\s+)?other\b/.test(text) ||
    /\bspaced\b|\bspread(?:\s+out)?\b|\bapart\b|\bseparate(?:ly)?\b/.test(text)
  ) {
    layout = 'line';
    if (/\bspaced\b|\bspread(?:\s+out)?\b|\bapart\b|\bseparate(?:ly)?\b/.test(text)) {
      spacing = 2.5;
    }
  }

  // Default: never pile quantity>1 at the same point
  if (!layout && quantity > 1) {
    layout = 'line';
  }

  if (!layout) return null;

  const axis = detectOrientationAxis(text, layout) || 'x';

  return {
    layout,
    axis,
    spacing,
    cols,
    quantity:
      quantity > 1 ? quantity : quantityHint > 1 ? quantityHint : quantity,
  };
}

/**
 * Resolve line/stack axis from orientation words in the command.
 * Returns null when the command has no clear orientation cue.
 *
 * Priority: vertical/Y > depth/Z > horizontal/X
 * so "straight line vertically" → y (not x).
 */
export function detectOrientationAxis(userCommand, layout = 'line') {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return null;

  if (layout === 'stack') return 'y';
  if (layout === 'circle' || layout === 'grid') return null;

  // Vertical / up
  if (
    /\bvertical(?:ly)?\b/.test(text) ||
    /\bupwards?\b/.test(text) ||
    /\bgoing\s+up\b/.test(text) ||
    /\bcolumns?\b/.test(text) ||
    /\bin\s+a\s+column\b/.test(text) ||
    /\bone\s+above\s+(?:the\s+)?other\b/.test(text) ||
    /\balong\s+(?:the\s+)?y(?:[\s-]?axis)?\b/.test(text) ||
    /\by[\s-]?axis\b/.test(text) ||
    /\btop\s+to\s+bottom\b/.test(text) ||
    /\bbottom\s+to\s+top\b/.test(text)
  ) {
    return 'y';
  }

  // Depth / front-back
  if (
    /\balong\s+(?:the\s+)?z(?:[\s-]?axis)?\b/.test(text) ||
    /\bz[\s-]?axis\b/.test(text) ||
    /\bfront\s+to\s+back\b/.test(text) ||
    /\bback\s+to\s+front\b/.test(text) ||
    /\binto\s+the\s+(?:scene|distance|depth)\b/.test(text) ||
    /\bdepth\b/.test(text)
  ) {
    return 'z';
  }

  // Horizontal / left-right
  if (
    /\bhorizontal(?:ly)?\b/.test(text) ||
    /\bside\s+by\s+side\b/.test(text) ||
    /\bin\s+a\s+row\b/.test(text) ||
    /\bleft\s+to\s+right\b/.test(text) ||
    /\bright\s+to\s+left\b/.test(text) ||
    /\balong\s+(?:the\s+)?x(?:[\s-]?axis)?\b/.test(text) ||
    /\bx[\s-]?axis\b/.test(text)
  ) {
    return 'x';
  }

  return null;
}

export function normalizeGroupLayout(value) {
  const v = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const map = {
    line: 'line',
    row: 'line',
    straight_line: 'line',
    straight: 'line',
    column: 'line',
    stack: 'stack',
    stacked: 'stack',
    circle: 'circle',
    ring: 'circle',
    grid: 'grid',
    matrix: 'grid',
    spread: 'line',
    spaced: 'line',
  };
  return map[v] || null;
}

/**
 * World positions for N objects under a layout, centered on origin.
 */
export function computeGroupPositions(count, layoutConfig = {}, origin = null) {
  const n = Math.max(1, Math.floor(Number(count) || 1));
  const layout = layoutConfig.layout || 'line';
  const axis = layoutConfig.axis === 'y' || layoutConfig.axis === 'z'
    ? layoutConfig.axis
    : 'x';
  const spacing = Number(layoutConfig.spacing);
  const gap = Number.isFinite(spacing) && spacing > 0 ? spacing : 1.5;
  const base = {
    x: Number(origin?.x) || 0,
    y: Number(origin?.y) || 0,
    z: Number(origin?.z) || 0,
  };

  const positions = [];

  if (layout === 'stack') {
    for (let i = 0; i < n; i++) {
      positions.push({
        x: base.x,
        y: base.y + i * gap,
        z: base.z,
      });
    }
    return positions;
  }

  if (layout === 'circle') {
    const radius = Math.max(gap, (n * gap) / (2 * Math.PI));
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      positions.push({
        x: base.x + Math.cos(a) * radius,
        y: base.y,
        z: base.z + Math.sin(a) * radius,
      });
    }
    return positions;
  }

  if (layout === 'grid') {
    const cols = Math.max(
      1,
      Math.floor(Number(layoutConfig.cols) || Math.ceil(Math.sqrt(n)))
    );
    const rows = Math.ceil(n / cols);
    for (let i = 0; i < n; i++) {
      const row = Math.floor(i / cols);
      const col = i % cols;
      positions.push({
        x: base.x + (col - (cols - 1) / 2) * gap,
        y: base.y,
        z: base.z + (row - (rows - 1) / 2) * gap,
      });
    }
    return positions;
  }

  // line / row (default)
  const start = -((n - 1) * gap) / 2;
  for (let i = 0; i < n; i++) {
    const offset = start + i * gap;
    const p = { x: base.x, y: base.y, z: base.z };
    p[axis] = (p[axis] || 0) + offset;
    positions.push(p);
  }
  return positions;
}

/**
 * Prefer spacing from the real object size so shapes don't overlap.
 * Uses height for vertical lines/stacks, width/depth for horizontal lines.
 */
export function spacingFromObject(
  object,
  layout = 'line',
  fallback = 1.5,
  axis = 'x'
) {
  if (!object) return fallback;
  try {
    object.updateMatrixWorld?.(true);
    const box = new THREE.Box3().setFromObject(object);
    if (box.isEmpty()) return fallback;
    const size = box.getSize(new THREE.Vector3());
    let dim;
    if (layout === 'stack' || axis === 'y') {
      dim = size.y;
    } else if (axis === 'z') {
      dim = size.z;
    } else {
      dim = Math.max(size.x, size.z);
    }
    const computed = dim + 0.45;
    return Number.isFinite(computed) && computed > 0.25 ? computed : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Inject layout + quantity into add_object calls from the user command.
 * Command orientation words always win over a wrong AI layoutAxis
 * (e.g. "straight line vertically" → axis y, not x).
 * Never treats angle numbers (90°) as quantity.
 */
export function enrichGroupLayoutToolCalls(userCommand, toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) return toolCalls;

  const cmd = String(userCommand || '');
  const qtyFromText = parseQuantityFromText(cmd);
  const orientationFromCommand = detectOrientationAxis(cmd);
  const angleValues = [
    ...cmd.matchAll(/(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)/gi),
  ].map((m) => Math.round(Math.abs(parseFloat(m[1]))));
  const singularAdd = /\b(?:add|create|make|place|put)\s+(?:a|an)\b/i.test(cmd);

  return toolCalls.map((call) => {
    if (!call || call.name !== 'add_object') return call;

    const params = { ...(call.parameters || {}) };
    let quantity = Number(params.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

    // Only raise quantity from a real count phrase — never from "90°"
    if (qtyFromText && qtyFromText > 1) {
      quantity = qtyFromText;
      params.quantity = qtyFromText;
    } else if (
      // AI sometimes puts the degree number into quantity
      quantity > 1 &&
      (singularAdd ||
        (!qtyFromText && angleValues.includes(Math.round(quantity))))
    ) {
      quantity = 1;
      params.quantity = 1;
      delete params.layout;
      delete params.layoutAxis;
      delete params.spacing;
      delete params.layoutCols;
    }

    const parsed = parseGroupLayout(cmd, quantity);
    if (!parsed) return { ...call, parameters: params };

    if (parsed.quantity > 1 && qtyFromText && qtyFromText > 1) {
      params.quantity = parsed.quantity;
      quantity = parsed.quantity;
    }

    if (quantity > 1) {
      if (!params.layout) {
        params.layout = parsed.layout || 'line';
      } else {
        params.layout =
          normalizeGroupLayout(params.layout) || params.layout;
      }

      // Orientation from the user command overrides AI / defaults
      if (orientationFromCommand) {
        params.layoutAxis = orientationFromCommand;
        if (
          orientationFromCommand === 'y' &&
          (params.layout === 'line' || !params.layout)
        ) {
          params.layout = params.layout || 'line';
        }
      } else if (!params.layoutAxis && parsed.axis) {
        params.layoutAxis = parsed.axis;
      } else if (!params.layoutAxis) {
        params.layoutAxis = 'x';
      }

      if (params.spacing == null && parsed.spacing != null) {
        params.spacing = parsed.spacing;
      }
      if (parsed.cols && params.layoutCols == null) {
        params.layoutCols = parsed.cols;
      }
    }

    return { ...call, parameters: params };
  });
}
